import React from 'react';
import { Card, CardContent } from '@/components/ui';
import { useAdminDashboard } from '../hooks/useDashboard';

interface KpiCardProps {
  title: string;
  value: string | number;
  trend?: string;
  trendType?: 'up' | 'down';
  icon: string;
  iconBg: string;
  iconColor: string;
  subtext?: string;
  progress?: number;
  progressLabel?: string;
}

function KpiCard({ title, value, trend, trendType, icon, iconBg, iconColor, subtext, progress, progressLabel }: KpiCardProps) {
  return (
    <Card className="border-none shadow-sm overflow-hidden h-full rounded-md">
      <CardContent className="p-3 flex flex-col justify-between h-full">
        <div className="flex justify-between items-start mb-3">
          <div className={`w-10 h-10 rounded-md ${iconBg} flex items-center justify-center shrink-0`}>
            <i className={`${icon} ${iconColor} text-lg`} />
          </div>
          <div className="text-right min-w-0">
            <p className="text-xs font-medium text-slate-400 mb-0.5">{title}</p>
            <p className="text-xl font-medium tracking-tight text-slate-800 leading-none">{value}</p>
          </div>
        </div>

        {progress !== undefined ? (
          <div className="space-y-1.5 mt-auto">
            <div className="flex justify-between items-center text-xs font-medium">
              <span className="text-slate-400">{progressLabel}</span>
              <span className="text-slate-600">{progress}%</span>
            </div>
            <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
              <div className="h-full bg-blue-500 rounded-full" style={{ width: `${progress}%` }} />
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 mt-auto">
            {trend && (
              <div className={`flex items-center text-xs font-medium ${trendType === 'up' ? 'text-emerald-600' : 'text-red-500'}`}>
                <i className={trendType === 'up' ? 'ri-arrow-up-line' : 'ri-arrow-down-line'} />
                <span>{trend}</span>
              </div>
            )}
            <span className="text-xs font-medium text-slate-400">{subtext}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function AdminKpiGrid() {
  const { data, isLoading } = useAdminDashboard();

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
      <KpiCard 
        title="Người dùng" 
        value={isLoading ? '...' : data?.totalUsers || 0} 
        trend="Realtime" 
        trendType="up" 
        subtext="so với tháng trước" 
        icon="ri-user-3-line" 
        iconBg="bg-blue-50" 
        iconColor="text-blue-500" 
      />
      <KpiCard 
        title="Vai trò Hệ thống" 
        value={isLoading ? '...' : data?.totalRoles || 0} 
        trend="Realtime" 
        trendType="up" 
        subtext="so với tháng trước" 
        icon="ri-shield-user-line" 
        iconBg="bg-purple-50" 
        iconColor="text-purple-500" 
      />
      <KpiCard 
        title="Tổ chức/Phòng ban" 
        value={isLoading ? '...' : data?.totalCompanies || 0} 
        trend="Realtime" 
        trendType="up" 
        subtext="so với tháng trước" 
        icon="ri-community-line" 
        iconBg="bg-emerald-50" 
        iconColor="text-emerald-500" 
      />
      <KpiCard 
        title="Dự án hệ thống" 
        value="162" 
        trend="8%" 
        trendType="up" 
        subtext="so với tháng trước" 
        icon="ri-folder-open-line" 
        iconBg="bg-orange-50" 
        iconColor="text-orange-500" 
      />
      <KpiCard 
        title="Lưu trữ" 
        value="256.7 GB" 
        progress={64}
        progressLabel="Đã dùng 64%"
        icon="ri-database-2-line" 
        iconBg="bg-blue-50" 
        iconColor="text-blue-500" 
      />
      <KpiCard 
        title="Hệ thống" 
        value={isLoading ? '...' : data?.systemStatus || 'Tốt'} 
        subtext="Hoạt động ổn định" 
        icon="ri-shield-check-line" 
        iconBg="bg-emerald-50" 
        iconColor="text-emerald-500" 
      />
    </div>
  );
}
